<template>
  <HeadlessPopover
    v-slot="{ open }"
    class="relative"
  >
    <HeadlessPopoverButton
      class="relative w-full h-full overflow-hidden rounded-xl border-2 border-transparent shadow-main outline-none transition-colors focus:border-orange-200"
      :class="{
        '!border-orange-400': open,
      }"
      :disabled="locked"
    >
      <div
        class="w-full select-none bg-white pb-3 pl-4 pr-14 pt-7 text-start text-base transition-colors"
        :class="{
          '!border-red': errorMessage,
        }"
      >
        <span>
          <template v-if="value">
            {{ displayValue }}&nbsp;
          </template>
        </span>
      </div>

      <IconLock
        v-if="locked"
        class="absolute right-4 top-1/2 h-6 -translate-y-1/2"
      />

      <label
        class="pointer-events-none absolute left-4 top-3 select-none text-xs text-[#777675] transition-all"
        :class="{
          'text-red': errorMessage,
          '!top-5 !text-base': !value,
          '!top-3 !text-xs': value,
        }"
      >
        {{ errorMessage || label }}
      </label>
    </HeadlessPopoverButton>

    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0"
    >
      <HeadlessPopoverPanel
        v-slot="{ close }"
        class="absolute left-1/2 z-10 mt-3 -translate-x-1/2 transform px-4 sm:px-0"
      >
        <div class="overflow-hidden rounded-xl shadow-lg ring-1 ring-black/5">
          <VueDatePicker
            ref="datepicker"
            v-model="value"
            locale="ru"
            :format-locale="ru"
            select-text="Выбрать"
            cancel-text="Отменить"
            now-button-label="Сейчас"
            :day-names="['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']"
            :min-time="minTime"
            :max-time="props.maxTime"
            :min-date="props.minDate"
            :max-date="props.maxDate"
            :disabled-times="disabledTimes"
            prevent-min-max-navigation
            :disabled-week-days="props.disabledWeekDays"
            :minutes-increment="10"
            :minutes-grid-increment="10"
            :start-time="props.startTime"
            inline
            auto-apply
            partial-flow
            :flow="['calendar', 'time']"
            :enable-time-picker="props.withTime"
            @update:model-value="emit('dateChanged', value)"
            @internal-model-change="handleInternal"
          >
              <template #clock-icon>
                  <div class="accent-gradient-bg group rounded-xl text-white outline-none transition-all focus:ring-2 focus:ring-orange-200 focus:ring-offset-2 px-2 py-2 text-xs">Выбрать время с {{props.minTime.hours}}:{{('0'+props.minTime.minutes).slice(-2)}} до {{props.maxTime.hours}}:{{('0'+props.maxTime.minutes).slice(-2)}}</div>
              </template>
              <template #action-extra>
                  <div class="inline-block my-2 cursor-pointer accent-gradient-bg group rounded-xl text-white outline-none transition-all focus:ring-2 focus:ring-orange-200 focus:ring-offset-2 px-2 py-2 text-xs" @click="close">Сохранить и закрыть</div>
              </template>
              <template #calendar-icon>
                  Вернуться к выбору даты
              </template>
          </VueDatePicker>
        </div>
      </HeadlessPopoverPanel>
    </transition>
  </HeadlessPopover>
</template>

<script setup lang="ts">
import VueDatePicker, { type DatePickerInstance, type PartialTimeObj } from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { DateTime } from 'luxon'
import { ru } from 'date-fns/locale';
import { ref } from 'vue'

const emit = defineEmits(['dateChanged'])

const props = defineProps<{
    name: string
    label: string
    locked?: boolean
    withTime?: boolean
    minDate?: Date
    maxDate?: Date
    minTime?: PartialTimeObj
    maxTime?: PartialTimeObj
    disabledWeekDays?: number[] | string[]
    startTime?: PartialTimeObj | PartialTimeObj[]
}>()

const { name } = toRefs(props)

const { value, errorMessage } = useField<Date | undefined>(name)

const datepicker = ref<DatePickerInstance>(null);

let disabledTimes = [];
// простое закрытие не работает, если выбирается только дата. значение не присваивается в model
const dateClicked = (date) => {
    if(!props.withTime) {
        return
    }

    let now = new Date();

    disabledTimes = []
    if(date && date.toDateString() == now.toDateString()) {
        for (let i = 0; i < props.minDate.getHours(); i++) {
            disabledTimes.push({ hours: i, minutes: "*" })
        }
        for (let i = 0; i < props.minDate.getMinutes(); i+=10) {
            disabledTimes.push({ hours: props.minDate.getHours(), minutes: i })
        }

        minTime.value = {hours: props.minDate.getHours(), minutes: props.minDate.getMinutes()}
        if(now.getHours() > date.getHours() && now.getMinutes() > date.getMinutes() ) {
            date.setHours(props.minDate.getHours())
            date.setMinutes(props.minDate.getMinutes())
            datepicker.value.updateInternalModelValue(date)
        }

        /*if(date.getHours() < now.getHours()) {
            if (datepicker) {
                let d = new Date()
                d.setHours(now.getHours()+1)
                datepicker.value.updateInternalModelValue(d)
            }
        }*/

    } else {
        minTime.value = props.minTime
    }
}

let minTime = ref(props.minTime)
let internalDate: Date
const handleInternal = (date) => {
    internalDate = date
    dateClicked(date)
}

/*
const disabledTimes = (selectedTime: PartialTimeObj) => {
    let now = new Date();

    if (internalDate.toDateString() == now.toDateString() && selectedTime.hours != undefined && selectedTime.minutes != undefined) {
        //internalDate.setHours(parseInt(selectedTime?.hours.toString()), parseInt(selectedTime?.minutes.toString()))
        console.log(internalDate < now)
        return internalDate < now
    }

    return false
};*/

const displayValue = computed(() => {
    let formatOptions = <Intl.DateTimeFormatOptions>{
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
    }
    if(props.withTime) {
        formatOptions.hour = '2-digit'
        formatOptions.minute = '2-digit'
    }

  if (value.value) {
    return DateTime.fromJSDate(new Date(value.value)).toLocaleString(formatOptions, {locale: 'ru'})
  }
  return ''
})
</script>

<style>
.dp__theme_light {
  --dp-background-color: #fff;
  --dp-text-color: #212121;
  --dp-hover-color: #f3f3f3;
  --dp-hover-text-color: #212121;
  --dp-hover-icon-color: #959595;
  --dp-primary-color: theme(colors.orange.400);
  --dp-primary-disabled-color: theme(colors.orange.100);
  --dp-primary-text-color: #f8f5f5;
  --dp-secondary-color: #c0c4cc;
  --dp-border-color: transparent;
  --dp-menu-border-color: #ddd;
  --dp-border-color-hover: #aaaeb7;
  --dp-disabled-color: #f6f6f6;
  --dp-scroll-bar-background: #f3f3f3;
  --dp-scroll-bar-color: #959595;
  --dp-success-color: #76d275;
  --dp-success-color-disabled: #a3d9b1;
  --dp-icon-color: #959595;
  --dp-danger-color: #ff6f60;
  --dp-marker-color: #ff6f60;
  --dp-tooltip-color: #fafafa;
  --dp-disabled-color-text: #8e8e8e;
  --dp-highlight-color: rgb(25 118 210 / 10%);
  --dp-range-between-dates-background-color: var(--dp-hover-color, #f3f3f3);
  --dp-range-between-dates-text-color: var(--dp-hover-text-color, #212121);
  --dp-range-between-border-color: var(--dp-hover-color, #f3f3f3);
}
.dp--time-invalid {
    background: #ffd1d1;
}
:root {
  --dp-font-family: 'Ubuntu', sans-serif;
  --dp-border-radius: theme('borderRadius.xl'); /*Configurable border-radius*/
  --dp-cell-border-radius: theme('borderRadius.full');
  --dp-animation-duration: 0.25s;
  --dp-action-buttons-padding: theme('padding.3') theme('padding.4');
  --dp-row-margin:  0.4rem 0;
  --dp-menu-min-width: 300px;
}

</style>
